import React, { Component } from 'react';
import { Card, Row, Col, Statistic, Table, Select, DatePicker, Button, Space, Tag, Progress, Tooltip, Badge, Avatar, Timeline } from 'antd';
import {
	PlayCircleOutlined,
	EyeOutlined,
	ClockCircleOutlined,
	UserOutlined,
	TrophyOutlined,
	RiseOutlined,
	FallOutlined,
	LineChartOutlined,
	BarChartOutlined,
	PieChartOutlined,
	DownloadOutlined,
	ReloadOutlined,
	CalendarOutlined,
	GlobalOutlined,
	VideoCameraOutlined,
	HeartOutlined,
	MessageOutlined,
	ShareAltOutlined,
	StarOutlined,
	ThunderboltOutlined
} from '@ant-design/icons';
import { Line, Bar, Pie, Area } from '@ant-design/charts';
import './StreamStats.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

class StreamStats extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: false,
			refreshing: false,
			
			// Filters
			timeRange: 'today',
			category: 'all',
			region: 'all',
			dateRange: null,
			
			// Statistics
			overallStats: {
				totalStreams: 0,
				activeStreams: 0,
				totalViewers: 0,
				peakViewers: 0,
				totalWatchTime: 0,
				avgStreamDuration: 0,
				newStreamers: 0,
				topCategories: []
			},
			
			// Charts data
			viewerTrendData: [],
			streamCategoryData: [],
			regionData: [],
			hourlyData: [],
			
			// Top performers
			topStreamers: [],
			topStreams: [],
			
			// Recent activities
			recentActivities: []
		};
	}

	componentDidMount() {
		this.loadStreamStats();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.streams !== this.props.streams) {
			this.loadStreamStats();
		}
	}

	// Load stream statistics
	loadStreamStats = () => {
		this.setState({ loading: true });
		
		Promise.all([
			this.loadOverallStats(),
			this.loadChartData(),
			this.loadTopPerformers(),
			this.loadRecentActivities()
		]).finally(() => {
			this.setState({ loading: false });
		});
	}

	// Load overall statistics
	loadOverallStats = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				const { streams } = this.props;
				const activeStreams = streams?.filter(s => s.status === 'live') || [];
				
				this.setState({
					overallStats: {
						totalStreams: streams?.length || 0,
						activeStreams: activeStreams.length,
						totalViewers: activeStreams.reduce((sum, s) => sum + s.viewerCount, 0),
						peakViewers: 15420, // Mock peak viewers
						totalWatchTime: 892340, // Mock total watch time in minutes
						avgStreamDuration: 127, // Mock average duration in minutes
						newStreamers: 23, // Mock new streamers today
						topCategories: [
							{ name: 'Gaming', count: 45, percentage: 42 },
							{ name: 'Music', count: 28, percentage: 26 },
							{ name: 'Talk Shows', count: 18, percentage: 17 },
							{ name: 'Education', count: 12, percentage: 11 },
							{ name: 'Art', count: 4, percentage: 4 }
						]
					}
				});
				resolve();
			}, 300);
		});
	}

	// Load chart data
	loadChartData = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				// Generate viewer trend data
				const viewerTrend = Array.from({ length: 24 }, (_, i) => ({
					hour: `${i.toString().padStart(2, '0')}:00`,
					viewers: Math.floor(Math.random() * 5000) + 2000,
					streams: Math.floor(Math.random() * 50) + 20
				}));

				// Generate category data
				const categoryData = [
					{ category: 'Gaming', streams: 45, viewers: 8500, percentage: 42 },
					{ category: 'Music', streams: 28, viewers: 5200, percentage: 26 },
					{ category: 'Talk Shows', streams: 18, viewers: 3400, percentage: 17 },
					{ category: 'Education', streams: 12, viewers: 2200, percentage: 11 },
					{ category: 'Art', streams: 4, viewers: 800, percentage: 4 }
				];

				// Generate region data
				const regionData = [
					{ region: 'North America', viewers: 6800, percentage: 34 },
					{ region: 'Europe', viewers: 5200, percentage: 26 },
					{ region: 'Asia Pacific', viewers: 4600, percentage: 23 },
					{ region: 'South America', viewers: 2100, percentage: 10.5 },
					{ region: 'Others', viewers: 1300, percentage: 6.5 }
				];

				this.setState({
					viewerTrendData: viewerTrend,
					streamCategoryData: categoryData,
					regionData: regionData,
					hourlyData: viewerTrend
				});
				resolve();
			}, 400);
		});
	}

	// Load top performers
	loadTopPerformers = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				const topStreamers = Array.from({ length: 10 }, (_, i) => ({
					id: `streamer-${i + 1}`,
					name: `Streamer${i + 1}`,
					avatar: null,
					category: ['Gaming', 'Music', 'Talk Shows'][Math.floor(Math.random() * 3)],
					followers: Math.floor(Math.random() * 50000) + 10000,
					totalViews: Math.floor(Math.random() * 1000000) + 100000,
					avgViewers: Math.floor(Math.random() * 2000) + 500,
					streamHours: Math.floor(Math.random() * 200) + 50,
					growth: (Math.random() * 50 - 25).toFixed(1) // -25% to +25%
				}));

				const topStreams = Array.from({ length: 10 }, (_, i) => ({
					id: `stream-${i + 1}`,
					title: `Amazing Stream ${i + 1}`,
					streamerName: `Streamer${i + 1}`,
					category: ['Gaming', 'Music', 'Talk Shows'][Math.floor(Math.random() * 3)],
					currentViewers: Math.floor(Math.random() * 3000) + 500,
					peakViewers: Math.floor(Math.random() * 5000) + 1000,
					duration: Math.floor(Math.random() * 300) + 60,
					likes: Math.floor(Math.random() * 1000) + 100,
					comments: Math.floor(Math.random() * 500) + 50,
					shares: Math.floor(Math.random() * 100) + 10
				}));

				this.setState({ topStreamers, topStreams });
				resolve();
			}, 350);
		});
	}

	// Load recent activities
	loadRecentActivities = () => {
		return new Promise((resolve) => {
			setTimeout(() => {
				const activities = [
					{
						id: 1,
						type: 'stream_started',
						user: 'TopStreamer',
						message: 'started streaming "Epic Gaming Session"',
						timestamp: new Date(Date.now() - 300000).toISOString(),
						viewers: 1250
					},
					{
						id: 2,
						type: 'milestone',
						user: 'MusicMaster',
						message: 'reached 10K followers',
						timestamp: new Date(Date.now() - 600000).toISOString(),
						milestone: '10K'
					},
					{
						id: 3,
						type: 'peak_viewers',
						user: 'TalkShowHost',
						message: 'achieved new peak of 3,500 viewers',
						timestamp: new Date(Date.now() - 900000).toISOString(),
						viewers: 3500
					},
					{
						id: 4,
						type: 'category_trending',
						message: 'Gaming category trending with 45 active streams',
						timestamp: new Date(Date.now() - 1200000).toISOString(),
						category: 'Gaming'
					},
					{
						id: 5,
						type: 'stream_ended',
						user: 'ArtCreator',
						message: 'ended 4-hour art stream with 800 viewers',
						timestamp: new Date(Date.now() - 1500000).toISOString(),
						duration: 240
					}
				];

				this.setState({ recentActivities: activities });
				resolve();
			}, 200);
		});
	}

	// Handle refresh
	handleRefresh = () => {
		this.setState({ refreshing: true });
		this.loadStreamStats().finally(() => {
			this.setState({ refreshing: false });
		});
	}

	// Handle time range change
	handleTimeRangeChange = (value) => {
		this.setState({ timeRange: value }, () => {
			this.loadStreamStats();
		});
	}

	// Handle category filter change
	handleCategoryChange = (value) => {
		this.setState({ category: value }, () => {
			this.loadStreamStats();
		});
	}

	// Handle region filter change
	handleRegionChange = (value) => {
		this.setState({ region: value }, () => {
			this.loadStreamStats();
		});
	}

	// Format duration
	formatDuration = (minutes) => {
		const hours = Math.floor(minutes / 60);
		const mins = minutes % 60;
		return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
	}

	// Format number
	formatNumber = (num) => {
		if (num >= 1000000) {
			return `${(num / 1000000).toFixed(1)}M`;
		}
		if (num >= 1000) {
			return `${(num / 1000).toFixed(1)}K`;
		}
		return Math.floor(num).toString();
	}

	// Get activity icon
	getActivityIcon = (type) => {
		const icons = {
			stream_started: <PlayCircleOutlined style={{ color: '#22c55e' }} />,
			milestone: <TrophyOutlined style={{ color: '#f59e0b' }} />,
			peak_viewers: <RiseOutlined style={{ color: '#3b82f6' }} />,
			category_trending: <ThunderboltOutlined style={{ color: '#8b5cf6' }} />,
			stream_ended: <ClockCircleOutlined style={{ color: '#6b7280' }} />
		};
		return icons[type] || <PlayCircleOutlined />;
	}

	// Render overview statistics
	renderOverviewStats = () => {
		const { overallStats } = this.state;

		return (
			<Row gutter={[16, 16]} className="overview-stats">
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="Active Streams"
							value={overallStats.activeStreams}
							prefix={<PlayCircleOutlined />}
							valueStyle={{ color: '#22c55e' }}
						/>
						<div className="stat-subtitle">
							of {overallStats.totalStreams} total streams
						</div>
					</Card>
				</Col>
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="Current Viewers"
							value={overallStats.totalViewers}
							prefix={<EyeOutlined />}
							valueStyle={{ color: '#3b82f6' }}
						/>
						<div className="stat-subtitle">
							Peak: {this.formatNumber(overallStats.peakViewers)}
						</div>
					</Card>
				</Col>
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="Watch Time"
							value={this.formatNumber(overallStats.totalWatchTime)}
							suffix="min"
							prefix={<ClockCircleOutlined />}
							valueStyle={{ color: '#f59e0b' }}
						/>
						<div className="stat-subtitle">
							Avg: {this.formatDuration(overallStats.avgStreamDuration)}
						</div>
					</Card>
				</Col>
				<Col span={6}>
					<Card size="small">
						<Statistic
							title="New Streamers"
							value={overallStats.newStreamers}
							prefix={<UserOutlined />}
							valueStyle={{ color: '#8b5cf6' }}
						/>
						<div className="stat-subtitle">
							Today
						</div>
					</Card>
				</Col>
			</Row>
		);
	}

	// Render viewer trend chart
	renderViewerTrendChart = () => {
		const { viewerTrendData } = this.state;

		const config = {
			data: viewerTrendData,
			xField: 'hour',
			yField: 'viewers',
			smooth: true,
			color: '#3b82f6',
			point: {
				size: 3,
				shape: 'circle'
			},
			area: {
				style: {
					fill: 'l(270) 0:#3b82f6 0.5:#3b82f610 1:#ffffff00'
				}
			}
		};

		return (
			<Card title="Viewer Trends (24h)" className="chart-card">
				<Area {...config} height={300} />
			</Card>
		);
	}

	// Render category distribution chart
	renderCategoryChart = () => {
		const { streamCategoryData } = this.state;

		const config = {
			data: streamCategoryData,
			angleField: 'viewers',
			colorField: 'category',
			radius: 0.8,
			interactions: [{ type: 'element-active' }]
		};

		return (
			<Card title="Category Distribution" className="chart-card">
				<Pie {...config} height={300} />
			</Card>
		);
	}

	// Render top streamers table
	renderTopStreamersTable = () => {
		const { topStreamers } = this.state;

		const columns = [
			{
				title: 'Rank',
				key: 'rank',
				width: 60,
				render: (_, __, index) => (
					<div className="rank-cell">
						{index < 3 ? (
							<TrophyOutlined 
								style={{ 
									color: index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : '#cd7f32' 
								}} 
							/>
						) : (
							<span className="rank-number">{index + 1}</span>
						)}
					</div>
				)
			},
			{
				title: 'Streamer',
				key: 'streamer',
                width: 150,
			    fixed: 'left',
				render: (_, record) => (
					<div className="streamer-info">
						<Avatar size="small" icon={<UserOutlined />} />
						<div className="streamer-details">
							<div className="streamer-name">{record.name}</div>
							<Tag size="small">{record.category}</Tag>
						</div>
					</div>
				)
			},
			{
				title: 'Followers',
				dataIndex: 'followers',
				key: 'followers',
                width: 100,
			    responsive: ['md'],
				render: (followers) => this.formatNumber(followers),
				sorter: (a, b) => a.followers - b.followers
			},
			{
				title: 'Avg Viewers',
				dataIndex: 'avgViewers',
				key: 'avgViewers',
                width: 100,
				render: (viewers) => this.formatNumber(viewers),
				sorter: (a, b) => a.avgViewers - b.avgViewers
			},
			{
				title: 'Stream Hours',
				dataIndex: 'streamHours',
				key: 'streamHours',
                width: 80,
			    responsive: ['lg'],
				render: (hours) => `${hours}h`
			},
			{
				title: 'Growth',
				dataIndex: 'growth',
				key: 'growth',
                width: 80,
			    responsive: ['lg'],
				render: (growth) => (
					<span style={{ color: growth >= 0 ? '#22c55e' : '#ef4444' }}>
						{growth >= 0 ? <RiseOutlined /> : <FallOutlined />}
						{Math.abs(growth)}%
					</span>
				)
			}
		];

		return (
			<Card title="Top Streamers" className="table-card">
				<Table
					columns={columns}
					dataSource={topStreamers}
					rowKey="id"
					pagination={{ pageSize: 5, showSizeChanger: false }}
					size="small"
				/>
			</Card>
		);
	}

	// Render top streams table
	renderTopStreamsTable = () => {
		const { topStreams } = this.state;

		const columns = [
			{
				title: 'Stream',
				key: 'stream',
				render: (_, record) => (
					<div className="stream-info">
						<div className="stream-title">{record.title}</div>
						<div className="stream-meta">
							<span className="streamer-name">{record.streamerName}</span>
							<Tag size="small">{record.category}</Tag>
						</div>
					</div>
				)
			},
			{
				title: 'Viewers',
				key: 'viewers',
				render: (_, record) => (
					<div className="viewer-stats">
						<div className="current-viewers">
							<EyeOutlined style={{ marginRight: 4 }} />
							{this.formatNumber(record.currentViewers)}
						</div>
						<div className="peak-viewers">
							Peak: {this.formatNumber(record.peakViewers)}
						</div>
					</div>
				)
			},
			{
				title: 'Duration',
				dataIndex: 'duration',
				key: 'duration',
				render: (duration) => this.formatDuration(duration)
			},
			{
				title: 'Engagement',
				key: 'engagement',
				render: (_, record) => (
					<div className="engagement-stats">
						<Space size="small">
							<span>
								<HeartOutlined style={{ color: '#ef4444' }} />
								{this.formatNumber(record.likes)}
							</span>
							<span>
								<MessageOutlined style={{ color: '#3b82f6' }} />
								{this.formatNumber(record.comments)}
							</span>
							<span>
								<ShareAltOutlined style={{ color: '#22c55e' }} />
								{this.formatNumber(record.shares)}
							</span>
						</Space>
					</div>
				)
			}
		];

		return (
			<Card title="Top Streams" className="table-card">
				<Table
					columns={columns}
					dataSource={topStreams}
					rowKey="id"
					pagination={{ pageSize: 5, showSizeChanger: false }}
					size="small"
				/>
			</Card>
		);
	}

	// Render recent activities
	renderRecentActivities = () => {
		const { recentActivities } = this.state;

		return (
			<Card title="Recent Activities" className="activities-card">
				<Timeline>
					{recentActivities.map(activity => (
						<Timeline.Item
							key={activity.id}
							dot={this.getActivityIcon(activity.type)}
						>
							<div className="activity-item">
								<div className="activity-content">
									{activity.user && (
										<span className="activity-user">{activity.user} </span>
									)}
									<span className="activity-message">{activity.message}</span>
								</div>
								<div className="activity-time">
									{new Date(activity.timestamp).toLocaleTimeString()}
								</div>
							</div>
						</Timeline.Item>
					))}
				</Timeline>
			</Card>
		);
	}

	render() {
		const { loading, refreshing, timeRange, category, region } = this.state;

		return (
			<div className="stream-stats">
				{/* Header */}
				<div className="stats-header">
					<div className="header-left">
						<h3>Stream Statistics</h3>
						<p>Comprehensive analytics and performance metrics</p>
					</div>
					<div className="header-controls">
						<Space>
							<Select
								value={timeRange}
								onChange={this.handleTimeRangeChange}
								style={{ width: 120 }}
							>
								<Option value="today">Today</Option>
								<Option value="week">This Week</Option>
								<Option value="month">This Month</Option>
								<Option value="year">This Year</Option>
							</Select>
							<Select
								value={category}
								onChange={this.handleCategoryChange}
								style={{ width: 120 }}
								placeholder="Category"
							>
								<Option value="all">All Categories</Option>
								<Option value="gaming">Gaming</Option>
								<Option value="music">Music</Option>
								<Option value="talk">Talk Shows</Option>
								<Option value="education">Education</Option>
							</Select>
							<Select
								value={region}
								onChange={this.handleRegionChange}
								style={{ width: 120 }}
								placeholder="Region"
							>
								<Option value="all">All Regions</Option>
								<Option value="na">North America</Option>
								<Option value="eu">Europe</Option>
								<Option value="asia">Asia Pacific</Option>
								<Option value="sa">South America</Option>
							</Select>
							<Button
								icon={<ReloadOutlined spin={refreshing} />}
								onClick={this.handleRefresh}
								loading={refreshing}
							>
								Refresh
							</Button>
							<Button icon={<DownloadOutlined />}>
								Export
							</Button>
						</Space>
					</div>
				</div>

				{/* Overview Statistics */}
				{this.renderOverviewStats()}

				{/* Charts */}
				<Row gutter={[16, 16]} style={{ marginTop: 16 }}>
					<Col span={16}>
						{this.renderViewerTrendChart()}
					</Col>
					<Col span={8}>
						{this.renderCategoryChart()}
					</Col>
				</Row>

				{/* Tables and Activities */}
				<Row gutter={[16, 16]} style={{ marginTop: 16 }} align="stretch">
					<Col span={12}>
						{this.renderTopStreamersTable()}
					</Col>
					<Col span={12}>
						{this.renderTopStreamsTable()}
					</Col>
				</Row>
				<Row gutter={[16, 16]} style={{ marginTop: 16 }}>
					<Col span={24}>
						{this.renderRecentActivities()}
					</Col>
				</Row>
			</div>
		);
	}
}

export default StreamStats;
